import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, SearchIcon } from 'lucide-react';
export function ContractTypes({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState({
    name: '',
    description: '',
    approvalWorkflow: 'standard',
    isDefault: false,
    isActive: true,
    fields: [{
      name: 'Start Date',
      type: 'date',
      required: true
    }, {
      name: 'End Date',
      type: 'date',
      required: true
    }, {
      name: 'Value',
      type: 'currency',
      required: true
    }]
  });
  // Sample contract types
  const [contractTypes, setContractTypes] = useState([{
    id: 1,
    name: 'Service Agreement',
    description: 'For ongoing service relationships with vendors',
    approvalWorkflow: 'standard',
    isDefault: true,
    isActive: true,
    fields: [{
      name: 'Start Date',
      type: 'date',
      required: true
    }, {
      name: 'End Date',
      type: 'date',
      required: true
    }, {
      name: 'Value',
      type: 'currency',
      required: true
    }, {
      name: 'Service Level',
      type: 'select',
      required: true
    }, {
      name: 'Termination Notice Period',
      type: 'number',
      required: true
    }]
  }, {
    id: 2,
    name: 'Lease Agreement',
    description: 'For property and equipment leases',
    approvalWorkflow: 'extended',
    isDefault: false,
    isActive: true,
    fields: [{
      name: 'Start Date',
      type: 'date',
      required: true
    }, {
      name: 'End Date',
      type: 'date',
      required: true
    }, {
      name: 'Value',
      type: 'currency',
      required: true
    }, {
      name: 'Property Address',
      type: 'text',
      required: true
    }, {
      name: 'Security Deposit',
      type: 'currency',
      required: true
    }, {
      name: 'Monthly Payment',
      type: 'currency',
      required: true
    }]
  }, {
    id: 3,
    name: 'Software License',
    description: 'For software licenses and subscriptions',
    approvalWorkflow: 'standard',
    isDefault: false,
    isActive: true,
    fields: [{
      name: 'Start Date',
      type: 'date',
      required: true
    }, {
      name: 'End Date',
      type: 'date',
      required: true
    }, {
      name: 'Value',
      type: 'currency',
      required: true
    }, {
      name: 'Number of Licenses',
      type: 'number',
      required: true
    }, {
      name: 'License Type',
      type: 'select',
      required: true
    }, {
      name: 'Maintenance Included',
      type: 'boolean',
      required: false
    }]
  }, {
    id: 4,
    name: 'Purchase Agreement',
    description: 'For one-time purchases of goods',
    approvalWorkflow: 'simple',
    isDefault: false,
    isActive: true,
    fields: [{
      name: 'Purchase Date',
      type: 'date',
      required: true
    }, {
      name: 'Delivery Date',
      type: 'date',
      required: false
    }, {
      name: 'Value',
      type: 'currency',
      required: true
    }, {
      name: 'Warranty Period',
      type: 'text',
      required: false
    }]
  }, {
    id: 5,
    name: 'Employment Contract',
    description: 'For employee agreements',
    approvalWorkflow: 'hr',
    isDefault: false,
    isActive: false,
    fields: [{
      name: 'Start Date',
      type: 'date',
      required: true
    }, {
      name: 'End Date',
      type: 'date',
      required: false
    }, {
      name: 'Salary',
      type: 'currency',
      required: true
    }, {
      name: 'Position',
      type: 'text',
      required: true
    }, {
      name: 'Department',
      type: 'text',
      required: true
    }, {
      name: 'Notice Period',
      type: 'text',
      required: true
    }]
  }]);
  // Filter contract types based on search term
  const filteredTypes = contractTypes.filter(type => type.name.toLowerCase().includes(searchTerm.toLowerCase()) || type.description.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAddType = () => {
    setShowAddForm(true);
    setEditingType(null);
    setNewType({
      name: '',
      description: '',
      approvalWorkflow: 'standard',
      isDefault: false,
      isActive: true,
      fields: [{
        name: 'Start Date',
        type: 'date',
        required: true
      }, {
        name: 'End Date',
        type: 'date',
        required: true
      }, {
        name: 'Value',
        type: 'currency',
        required: true
      }]
    });
  };
  const handleEditType = type => {
    setShowAddForm(true);
    setEditingType(type);
    setNewType({
      ...type
    });
  };
  const handleToggleStatus = id => {
    setContractTypes(contractTypes.map(type => type.id === id ? {
      ...type,
      isActive: !type.isActive
    } : type));
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (editingType) {
      // Update existing type
      setContractTypes(contractTypes.map(type => type.id === editingType.id ? {
        ...newType,
        id: type.id
      } : type));
    } else {
      // Add new type
      setContractTypes([...contractTypes, {
        ...newType,
        id: Math.max(...contractTypes.map(t => t.id)) + 1
      }]);
    }
    setShowAddForm(false);
    setEditingType(null);
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setNewType({
      ...newType,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleAddField = () => {
    setNewType({
      ...newType,
      fields: [...newType.fields, {
        name: '',
        type: 'text',
        required: false
      }]
    });
  };
  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...newType.fields];
    updatedFields[index] = {
      ...updatedFields[index],
      [field]: field === 'required' ? value === 'true' : value
    };
    setNewType({
      ...newType,
      fields: updatedFields
    });
  };
  const handleRemoveField = index => {
    const updatedFields = [...newType.fields];
    updatedFields.splice(index, 1);
    setNewType({
      ...newType,
      fields: updatedFields
    });
  };
  const handleDelete = id => {
    if (window.confirm('Are you sure you want to delete this contract type?')) {
      setContractTypes(contractTypes.filter(type => type.id !== id));
    }
  };
  return <div data-id="element-2838">
      <div className="mb-8 flex justify-between items-center" data-id="element-2839">
        <div data-id="element-2840">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2841">Contract Types</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2842">
            Manage contract types and their required fields
          </p>
        </div>
        <button onClick={handleAddType} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2843">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2844" />
          Add Contract Type
        </button>
      </div>
      {/* Search */}
      {!showAddForm && <div className="mb-6" data-id="element-2845">
          <div className="max-w-md" data-id="element-2846">
            <div className="relative rounded-md shadow-sm" data-id="element-2847">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2848">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2849" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search contract types..." data-id="element-2850" />
            </div>
          </div>
        </div>}
      {/* Add/Edit Form */}
      {showAddForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2851">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2852">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2853">
              {/* Type Name */}
              <div className="sm:col-span-3" data-id="element-2854">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-2855">
                  Type Name
                </label>
                <div className="mt-1" data-id="element-2856">
                  <input type="text" name="name" id="name" value={newType.name} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2857" />
                </div>
              </div>
              {/* Approval Workflow */}
              <div className="sm:col-span-3" data-id="element-2858">
                <label htmlFor="approvalWorkflow" className="block text-sm font-medium text-gray-700" data-id="element-2859">
                  Approval Workflow
                </label>
                <div className="mt-1" data-id="element-2860">
                  <select id="approvalWorkflow" name="approvalWorkflow" value={newType.approvalWorkflow} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2861">
                    <option value="simple" data-id="element-2862">Simple (1 level)</option>
                    <option value="standard" data-id="element-2863">Standard (2 levels)</option>
                    <option value="extended" data-id="element-2864">Extended (3+ levels)</option>
                    <option value="hr" data-id="element-2865">HR Workflow</option>
                    <option value="legal" data-id="element-2866">Legal Workflow</option>
                  </select>
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2867">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2868">
                  Description
                </label>
                <div className="mt-1" data-id="element-2869">
                  <textarea id="description" name="description" rows={2} value={newType.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2870" />
                </div>
              </div>
              {/* Default and Active Checkboxes */}
              <div className="sm:col-span-6" data-id="element-2871">
                <div className="flex items-start space-x-4" data-id="element-2872">
                  <div className="flex items-center h-5" data-id="element-2873">
                    <input id="isDefault" name="isDefault" type="checkbox" checked={newType.isDefault} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-2874" />
                    <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700" data-id="element-2875">
                      Default type
                    </label>
                  </div>
                  <div className="flex items-center h-5" data-id="element-2876">
                    <input id="isActive" name="isActive" type="checkbox" checked={newType.isActive} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-2877" />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-2878">
                      Active
                    </label>
                  </div>
                </div>
              </div>
              {/* Fields Section */}
              <div className="sm:col-span-6" data-id="element-2879">
                <div className="pb-3 border-b border-gray-200" data-id="element-2880">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-2881">
                    Custom Fields
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-2882">
                    Define the fields required for this contract type
                  </p>
                </div>
                {/* Field List */}
                <div className="mt-4 space-y-4" data-id="element-2883">
                  {newType.fields.map((field, index) => <div key={index} className="flex items-center space-x-4" data-id="element-2884">
                      <div className="w-1/3" data-id="element-2885">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-2886">
                          Field Name
                        </label>
                        <input type="text" value={field.name} onChange={e => handleFieldChange(index, 'name', e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2887" />
                      </div>
                      <div className="w-1/4" data-id="element-2888">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-2889">
                          Field Type
                        </label>
                        <select value={field.type} onChange={e => handleFieldChange(index, 'type', e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2890">
                          <option value="text" data-id="element-2891">Text</option>
                          <option value="number" data-id="element-2892">Number</option>
                          <option value="date" data-id="element-2893">Date</option>
                          <option value="currency" data-id="element-2894">Currency</option>
                          <option value="select" data-id="element-2895">Dropdown</option>
                          <option value="boolean" data-id="element-2896">Yes/No</option>
                          <option value="textarea" data-id="element-2897">Text Area</option>
                        </select>
                      </div>
                      <div className="w-1/4" data-id="element-2898">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-2899">
                          Required
                        </label>
                        <select value={field.required.toString()} onChange={e => handleFieldChange(index, 'required', e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2900">
                          <option value="true" data-id="element-2901">Yes</option>
                          <option value="false" data-id="element-2902">No</option>
                        </select>
                      </div>
                      <div className="flex items-end pb-1" data-id="element-2903">
                        <button type="button" onClick={() => handleRemoveField(index)} className="text-red-600 hover:text-red-900" disabled={index < 3} // Don't allow removing the first 3 default fields
                  data-id="element-2904">
                          <TrashIcon className="h-5 w-5" data-id="element-2905" />
                        </button>
                      </div>
                    </div>)}
                  <button type="button" onClick={handleAddField} className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-2906">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-2907" />
                    Add Field
                  </button>
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2908">
              <div className="flex justify-end" data-id="element-2909">
                <button type="button" onClick={() => setShowAddForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-2910">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2911">
                  {editingType ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div> /* Contract Types List */ : <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2912">
          <div className="overflow-x-auto" data-id="element-2913">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2914">
              <thead className="bg-gray-50" data-id="element-2915">
                <tr data-id="element-2916">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2917">
                    Type Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2918">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2919">
                    Approval Workflow
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2920">
                    Fields
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2921">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2922">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2923">
                {filteredTypes.length === 0 ? <tr data-id="element-2924">
                    <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-2925">
                      No contract types found matching your criteria.
                    </td>
                  </tr> : filteredTypes.map(type => <tr key={type.id} className="hover:bg-gray-50" data-id="element-2926">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2927">
                        <div className="flex items-center" data-id="element-2928">
                          <div className="text-sm font-medium text-indigo-600" data-id="element-2929">
                            {type.name}
                            {type.isDefault && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2930">
                                Default
                              </span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2931">
                        {type.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2932">
                        {type.approvalWorkflow.charAt(0).toUpperCase() + type.approvalWorkflow.slice(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2933">
                        {type.fields.length} fields
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2934">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${type.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2935">
                          {type.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2936">
                        <div className="flex justify-end space-x-2" data-id="element-2937">
                          <button onClick={() => handleEditType(type)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-2938">
                            <EditIcon className="h-4 w-4" data-id="element-2939" />
                          </button>
                          <button onClick={() => handleToggleStatus(type.id)} className={`${type.isActive ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'}`} title={type.isActive ? 'Deactivate' : 'Activate'} data-id="element-2940">
                            {type.isActive ? <ToggleRightIcon className="h-4 w-4" data-id="element-2941" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-2942" />}
                          </button>
                          <button onClick={() => handleDelete(type.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-2943">
                            <TrashIcon className="h-4 w-4" data-id="element-2944" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}